import styled, { css } from 'styled-components';
import { customMedia } from 'styles/utils';
import { BlockSize } from 'libs/rendering/gltf/config';

import Icon from 'components/Icon';
import Button from 'components/Button';

export const TreeStyled = styled.nav`
  position: relative;
  z-index: 1003;
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
  background: ${props => props.theme.color.wfWhite};

  scroll-behavior: smooth;
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }
`;

export const NodeListStyled = styled.ul`
  color: ${props => props.theme.color.wfGrey};
  font-size: 0.925rem;
`;

export const ActionsDesktopStyled = styled.div`
  position: relative;
  display: flex;
  height: ${BlockSize.constTreeNodeHeight}px;
  margin-left: 0.5rem;

  ${customMedia.lessThan('medium')`
    display: none;
  `};
`;

export const ActionsBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;

  margin-left: 1px;
  cursor: pointer;

  ${customMedia.lessThan('medium')`
    min-width: ${BlockSize.width};
  `};
`;

export const ActionsMobileStyled = styled.div`
  display: none;

  ${customMedia.lessThan('medium')`
    position: relative;
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `};
`;

export const NodeStyled = styled.li`
  display: flex;
  flex-direction: column;
  height: ${BlockSize.constTreeNodeHeight}px;
`;

export const IconStyled = styled(Icon)`
  position: relative;
  transform: ${props => props.rotated && 'rotate(90deg)'};
  transition: transform linear 0.1s;

  border-radius: 3px;
  color: ${props =>
    props.isBody
      ? props.theme.color.wfBlue
      : props.isDrawing
      ? props.theme.color.wfPurple
      : props.theme.color.wfGrey};

  cursor: ${props => props.cursor && props.cursor};

  padding: 1.5px;
`;

export const ButtonStyled = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 0.5rem;
  height: ${BlockSize.constTreeNodeHeight}px;
  &:hover {
    color: ${props => props.theme.color.wfBlue};
  }

  svg {
    transform: scale(0.9);
  }
`;

export const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;

  opacity: ${props => (props.visible ? '1' : '0.5')};
  color: ${props =>
    props.selected
      ? props.theme.color.primary
      : props.highlighted && !props.isProduct
      ? props.theme.color.primaryDark
      : props.theme.color.wfBlack};
  background-color: ${props =>
    props.isProduct && props.hidesInstance
      ? props.theme.color.wfYellow200
      : 'transparent'};
  font-weight: ${props =>
    props.fontWeight || ((props.selected || props.highlighted) && 'bold')};

  ${props =>
    props.isCurrentRoot &&
    `
    color: ${props.theme.color.wfGrey};
    font-weight: ${props.fontWeight || 'bold'};
  `}

  ${customMedia.lessThan('medium')`
    height: ${BlockSize.constTreeNodeHeight}px;
    padding-left: ${props => props.level * BlockSize.width}px;
  `};
`;

export const RowContentWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  overflow: hidden;
  height: ${BlockSize.constTreeNodeHeight - 1}px;

  padding-right: 2px;
  cursor: ${props => (props.isCurrentRoot ? 'auto' : 'pointer')};
`;

export const NameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-weight: inherit;
  margin-left: 6px;

  p {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    font-weight: inherit;
    color: inherit;
  }

  border-radius: 40px;

  background-color: transparent;
  text-decoration: ${props =>
    props.isProduct ? 'none' : `underline ${props.theme.color.wfGrey500}`};

  &:hover {
    background-color: transparent;
  }
  pointer-events: auto;
`;

export const TreeLinesBlock = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: ${props => `0 0 0 ${props.theme.spacing.small}`};
`;

export const TreeLineStyled = styled.div`
  position: relative;
  height: ${BlockSize.constTreeNodeHeight}px;
  width: ${BlockSize.constTreeIndent}px;
  margin-left: 1px;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: ${0.5 * BlockSize.constTreeIndent - 1}px;
    border: 0;
    border-right: 1px solid ${props => props.theme.color.wfGrey400};
  }

  ${props =>
    props.hasTick &&
    css`
      &:before {
        content: '';
        position: absolute;
        top: 0;
        height: ${0.5 * BlockSize.constTreeNodeHeight}px;
        left: ${0.5 * BlockSize.constTreeIndent - 1}px;
        width: ${Math.max(2, 0.5 * BlockSize.constTreeIndent)}px;
        border: 0;
        border-bottom: 1px solid ${props.theme.color.wfGrey400};
      }
    `}
`;

export const NodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: inherit;
  margin-left: 5px;
`;

export const GridContentWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 22px 42px auto;

  justify-content: flex-start;
  align-items: center;

  border-top: 1px solid ${props => props.theme.color.wfGrey200};
  padding-right: 2px;
  cursor: ${props => (props.isCurrentRoot ? 'auto' : 'pointer')};
`;

export const PathWrapper = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;

  padding: 1px;
  border-right: 1px solid ${props => props.theme.color.wfGrey200};
  background: ${props => props.theme.color.wfBlue50};
  color: ${props => props.theme.color.wfBlue};
  font-family: 'Courier New', Courier, monospace;
`;

export const EnumStyled = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 100%;
  border-right: 1px solid ${props => props.theme.color.wfGrey200};
  background: ${props => props.theme.color.wfGrey50};
  color: ${props => props.theme.color.wfGrey};
  font-size: 13px;
`;
